<template>
    <!-- Desktop Modal -->
    <UiModal
        v-if="!isMobile"
        :isOpen="isOpen"
        @close="closeModal"
        title="Transfer calculator"
        width="480px"
    >
        <template #content>
            <div class="flex flex-col gap-8">
                <!-- Calculator Content -->
                <div
                    class="flex flex-col justify-center items-start w-full relative"
                >
                    <div
                        class="flex flex-col justify-center items-start self-stretch gap-2"
                    >
                        <!-- From Program -->
                        <div
                            class="flex p-8 flex-col justify-center items-center gap-4 self-stretch rounded-lg bg-slate-50 dark:bg-slate-900"
                        >
                            <div
                                class="flex w-full max-w-md flex-col items-start"
                            >
                                <div
                                    :class="[
                                        'flex h-10 p-0 pr-4 items-center gap-2 self-stretch rounded bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 focus-within:ring-1 focus-within:ring-slate-800  transition-all',
                                        hasInputError ? '!border-red-500' : '',
                                    ]"
                                >
                                    <!-- From Program Logo -->
                                    <div
                                        :class="[
                                            'flex w-26 h-10 px-4 justify-center items-center border-r border-slate-200 dark:border-slate-700 shrink-0',
                                            hasInputError
                                                ? '!border-red-500'
                                                : '',
                                        ]"
                                    >
                                        <img
                                            v-if="fromProgram?.program_image"
                                            :src="`${cdnUrl}/${fromProgram.program_image.path}/_small/${fromProgram.program_image.file_name}`"
                                            :alt="fromProgram.name"
                                            class="w-[80px] max-h-6 object-contain"
                                        />
                                        <span
                                            v-else
                                            class="text-xs font-medium text-slate-600 dark:text-slate-300"
                                        >
                                            {{
                                                fromProgram?.name?.slice(
                                                    0,
                                                    8
                                                ) || 'Program'
                                            }}
                                        </span>
                                    </div>

                                    <!-- Points Input -->
                                    <input
                                        v-model="formattedInput"
                                        type="text"
                                        placeholder="Points to transfer"
                                        @input="handleInputChange"
                                        @blur="handleBlur"
                                        @focus="
                                            clearError('points_to_transfer')
                                        "
                                        class="flex-1 text-slate-500 dark:text-slate-400 text-right text-sm font-normal leading-5 bg-transparent border-0 outline-none focus:outline-none focus:ring-0"
                                    />
                                </div>
                                <!-- Error Message -->
                                <div
                                    v-if="hasInputError"
                                    class="text-red-500 text-xs flex gap-2 py-1.5 mt-1"
                                    role="alert"
                                >
                                    <div class="flex justify-center">
                                        <Icon icon="caution" class="w-4 h-4" />
                                    </div>
                                    {{ calculatorErrors.points_to_transfer }}
                                </div>
                            </div>
                        </div>

                        <!-- To Program -->
                        <div
                            class="flex p-8 flex-col justify-center items-center gap-6 self-stretch rounded-lg bg-slate-50 dark:bg-slate-900"
                        >
                            <div
                                class="flex w-full max-w-md flex-col items-start"
                            >
                                <div
                                    class="flex h-10 p-0 pr-4 items-center gap-2 self-stretch rounded bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700"
                                >
                                    <!-- To Program Logo -->
                                    <div
                                        class="flex w-26 h-10 px-4 justify-center items-center border-r border-slate-200 dark:border-slate-700 shrink-0"
                                    >
                                        <img
                                            v-if="toProgram?.program_image"
                                            :src="`${cdnUrl}/${toProgram.program_image.path}/_small/${toProgram.program_image.file_name}`"
                                            :alt="toProgram.name"
                                            class="w-[80px] max-h-6 object-contain"
                                        />
                                        <span
                                            v-else
                                            class="text-xs font-medium text-slate-600 dark:text-slate-300"
                                        >
                                            {{
                                                toProgram?.name?.slice(0, 8) ||
                                                'Partner'
                                            }}
                                        </span>
                                    </div>

                                    <!-- Calculated Points -->
                                    <div
                                        class="flex-1 text-slate-500 dark:text-slate-400 text-right text-sm font-normal leading-5 overflow-hidden text-ellipsis whitespace-nowrap"
                                    >
                                        {{
                                            formattedCalculatedPoints ||
                                            'Points to receive'
                                        }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Divider with Arrow - Absolute positioning -->
                    <div
                        class="bg-white dark:bg-slate-800 rounded-full p-1 flex absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                    >
                        <div
                            class="w-10 h-10 flex justify-center items-center rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 z-10"
                        >
                            <Icon
                                icon="arrow-down"
                                class="w-4 h-4 text-slate-500 dark:text-slate-400"
                            />
                        </div>
                    </div>
                </div>

                <!-- Transfer Rate Section -->
                <div
                    class="flex w-full px-4 pt-0 flex-col justify-center items-center gap-4 rounded"
                >
                    <div class="flex h-5 items-center self-stretch gap-2">
                        <div class="flex items-center gap-2 flex-1 flex-wrap">
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 whitespace-nowrap"
                            >
                                Transfer rate
                            </span>
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 whitespace-nowrap"
                            >
                                <template v-if="bonusActive">
                                    <span class="line-through mr-2">
                                        {{ baseTransferRate }}
                                    </span>
                                    <span class="font-normal">
                                        {{ effectiveTransferRate }}
                                    </span>
                                </template>
                                <template v-else>
                                    {{ transferRate }}
                                </template>
                            </span>

                            <div
                                v-if="bonusActive"
                                class="inline-flex py-[2px] px-3 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 shrink-0"
                            >
                                <span
                                    class="text-slate-500 dark:text-slate-400 text-center text-xs font-medium leading-4 whitespace-nowrap"
                                >
                                    {{ bonusText }}
                                </span>
                            </div>
                        </div>

                    </div>

                    <!-- Summary Section -->
                    <div
                        v-if="hasEverBeenValid && !hasInputError"
                        class="flex pt-4 flex-col items-start gap-4 self-stretch border-t border-slate-200 dark:border-slate-700"
                    >
                        <div
                            class="flex justify-between items-center self-stretch"
                        >
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                            >
                                Standard rate {{ baseTransferRate }}
                            </span>
                            <span
                                class="text-slate-900 dark:text-slate-100 text-sm font-normal leading-5"
                            >
                                {{ standardPoints.toLocaleString('en-US') }}
                            </span>
                        </div>

                        <div
                            v-if="bonusActive"
                            class="flex justify-between items-center self-stretch"
                        >
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                            >
                                {{ bonusPercentageLabel }}
                            </span>
                            <span
                                class="text-slate-900 dark:text-slate-100 text-sm font-normal leading-5"
                            >
                                {{ bonusPoints.toLocaleString('en-US') }}
                            </span>
                        </div>

                        <div
                            class="flex justify-between items-center self-stretch"
                        >
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5"
                            >
                                Total you receive
                            </span>
                            <span
                                class="text-slate-900 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                {{ calculatedPoints.toLocaleString('en-US') }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </UiModal>

    <!-- Mobile Modal -->
    <UiModalMobile
        v-else
        :isOpen="isOpen"
        @close="closeModal"
        title="Transfer calculator"
    >
        <template #content>
            <div class="flex flex-col gap-6">
                <!-- Calculator Content -->
                <div
                    class="flex flex-col justify-center items-start w-full relative"
                >
                    <div
                        class="flex flex-col justify-center items-start self-stretch gap-2"
                    >
                        <!-- From Program -->
                        <div
                            class="flex p-6 flex-col justify-center items-center gap-4 self-stretch rounded-lg bg-slate-50 dark:bg-slate-900"
                        >
                            <div class="flex w-full flex-col items-start">
                                <div
                                    :class="[
                                        'flex h-10 p-0 items-center gap-2 self-stretch rounded bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 focus-within:ring-1 focus-within:ring-slate-800 transition-all',
                                        hasInputError ? '!border-red-500' : '',
                                    ]"
                                >
                                    <!-- From Program Logo -->
                                    <div
                                        :class="[
                                            'flex w-26 h-10 px-4 justify-center items-center border-r border-slate-200 dark:border-slate-700 shrink-0',
                                            hasInputError
                                                ? '!border-red-500'
                                                : '',
                                        ]"
                                    >
                                        <img
                                            v-if="fromProgram?.program_image"
                                            :src="`${cdnUrl}/${fromProgram.program_image.path}/_small/${fromProgram.program_image.file_name}`"
                                            :alt="fromProgram.name"
                                            class="w-[80px] max-h-6 object-contain"
                                        />
                                        <span
                                            v-else
                                            class="text-xs font-medium text-slate-600 dark:text-slate-300"
                                        >
                                            {{
                                                fromProgram?.name?.slice(
                                                    0,
                                                    8
                                                ) || 'Program'
                                            }}
                                        </span>
                                    </div>

                                    <!-- Points Input -->
                                    <input
                                        v-model="formattedInput"
                                        type="text"
                                        placeholder="Points to transfer"
                                        @input="handleInputChange"
                                        @blur="handleBlur"
                                        @focus="
                                            clearError('points_to_transfer')
                                        "
                                        class="flex-1 min-w-0 text-slate-500 dark:text-slate-400 text-right text-sm font-normal leading-5 bg-transparent border-0 outline-none focus:outline-none focus:ring-0"
                                    />
                                </div>
                                <!-- Error Message -->
                                <div
                                    v-if="hasInputError"
                                    class="text-red-500 text-xs flex gap-2 py-1.5 mt-1"
                                    role="alert"
                                >
                                    <div class="flex justify-center">
                                        <Icon icon="caution" class="w-4 h-4" />
                                    </div>
                                    {{ calculatorErrors.points_to_transfer }}
                                </div>
                            </div>
                        </div>

                        <!-- To Program -->
                        <div
                            class="flex p-6 flex-col justify-center items-center gap-6 self-stretch rounded-lg bg-slate-50 dark:bg-slate-900"
                        >
                            <div class="flex w-full flex-col items-start">
                                <div
                                    class="flex h-10 p-0 pr-4 items-center gap-2 self-stretch rounded bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700"
                                >
                                    <!-- To Program Logo -->
                                    <div
                                        class="flex w-26 h-10 px-4 justify-center items-center border-r border-slate-200 dark:border-slate-700 shrink-0"
                                    >
                                        <img
                                            v-if="toProgram?.program_image"
                                            :src="`${cdnUrl}/${toProgram.program_image.path}/_small/${toProgram.program_image.file_name}`"
                                            :alt="toProgram.name"
                                            class="w-[80px] max-h-6 object-contain"
                                        />
                                        <span
                                            v-else
                                            class="text-xs font-medium text-slate-600 dark:text-slate-300"
                                        >
                                            {{
                                                toProgram?.name?.slice(0, 8) ||
                                                'Partner'
                                            }}
                                        </span>
                                    </div>

                                    <!-- Calculated Points -->
                                    <div
                                        class="flex-1 min-w-0 text-slate-500 dark:text-slate-400 text-right text-sm font-normal leading-5 overflow-hidden text-ellipsis whitespace-nowrap"
                                    >
                                        {{
                                            formattedCalculatedPoints ||
                                            'Points to receive'
                                        }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Divider with Arrow - Absolute positioning -->
                    <div
                        class="bg-white dark:bg-slate-800 rounded-full p-1 flex absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                    >
                        <div
                            class="w-10 h-10 flex justify-center items-center rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 z-10"
                        >
                            <Icon
                                icon="arrow-down"
                                class="w-4 h-4 text-slate-500 dark:text-slate-400"
                            />
                        </div>
                    </div>
                </div>

                <!-- Transfer Rate Section -->
                <div
                    class="flex w-full pt-0 pb-5 flex-col justify-center items-center gap-4 rounded"
                >
                    <div class="flex h-5 items-center self-stretch gap-2">
                        <div class="flex items-center gap-2 flex-1 flex-wrap">
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 whitespace-nowrap"
                            >
                                Transfer rate
                            </span>
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 whitespace-nowrap"
                            >
                                <template v-if="bonusActive">
                                    <span class="line-through mr-2">
                                        {{ baseTransferRate }}
                                    </span>
                                    <span class="font-normal">
                                        {{ effectiveTransferRate }}
                                    </span>
                                </template>
                                <template v-else>
                                    {{ transferRate }}
                                </template>
                            </span>

                            <div
                                v-if="bonusActive"
                                class="inline-flex py-[2px] px-3 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 shrink-0"
                            >
                                <span
                                    class="text-slate-500 dark:text-slate-400 text-center text-xs font-medium leading-4 whitespace-nowrap"
                                >
                                    {{ bonusText }}
                                </span>
                            </div>
                        </div>

                    </div>

                    <!-- Summary Section -->
                    <div
                        v-if="hasEverBeenValid && !hasInputError"
                        class="flex pt-4 flex-col items-start gap-4 self-stretch border-t border-slate-200 dark:border-slate-700"
                    >
                        <div
                            class="flex justify-between items-center self-stretch"
                        >
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                            >
                                Standard rate {{ baseTransferRate }}
                            </span>
                            <span
                                class="text-slate-900 dark:text-slate-100 text-sm font-normal leading-5"
                            >
                                {{ standardPoints.toLocaleString('en-US') }}
                            </span>
                        </div>

                        <div
                            v-if="bonusActive"
                            class="flex justify-between items-center self-stretch"
                        >
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                            >
                                {{ bonusPercentageLabel }}
                            </span>
                            <span
                                class="text-slate-900 dark:text-slate-100 text-sm font-normal leading-5"
                            >
                                {{ bonusPoints.toLocaleString('en-US') }}
                            </span>
                        </div>

                        <div
                            class="flex justify-between items-center self-stretch"
                        >
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5"
                            >
                                Total you receive
                            </span>
                            <span
                                class="text-slate-900 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                {{ calculatedPoints.toLocaleString('en-US') }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </UiModalMobile>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import { useIsMobile } from '@/composables/useIsMobile';

const cdnUrl = window.cdnUrl;
import {
    bonusMultiplier,
    ratioDisplay,
    ratioDisplayPair,
} from '@/utils/transferBonus';

const { isMobile } = useIsMobile();

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    fromProgram: {
        type: Object,
        default: () => ({}),
    },
    toProgram: {
        type: Object,
        default: () => ({}),
    },
    transferPartner: {
        type: Object,
        default: () => ({}),
    },
});

const emit = defineEmits(['close']);

const pointsToTransfer = ref('');
const formattedInput = ref('');
const calculatorErrors = ref({});
const isValidating = ref(false);
const hasEverBeenValid = ref(false);
const debounceTimer = ref(null);
const lastLoggedKey = ref(null);

// --- Logging helpers ---
const currentLogKey = computed(() => {
    const f = props.fromProgram || {};
    const t = props.toProgram || {};
    const tp = props.transferPartner || {};
    const pts = pointsToTransfer.value || '';
    const bm = normalizedBonusMultiplier.value || '';
    return `${f.id || ''}-${t.id || ''}-${tp.id || ''}-${pts}-${bm}`;
});

function buildLogPayload() {
    const f = props.fromProgram || {};
    const t = props.toProgram || {};
    const tp = props.transferPartner || {};

    const params = {
        from_program_id: f.id ?? null,
        from_program_name: f.name ?? null,
        to_program_id: t.id ?? null,
        to_program_name: t.name ?? null,
        transfer_partner_id: tp.id ?? null,
        transfer_partner_name: tp.name ?? null,
        transfer_ratio: tp.transfer_ratio ?? null,
        bonus_multiplier: normalizedBonusMultiplier.value ?? null,
        bonus_active: bonusActive.value,
        bonus_end_date: tp.bonus_end_date ?? null,
        points_to_transfer: pointsToTransfer.value
            ? parseInt(pointsToTransfer.value)
            : null,
    };

    const response = {
        standard_points: standardPoints.value,
        bonus_points: bonusPoints.value,
        total_points: calculatedPoints.value,
        base_transfer_rate: baseTransferRate.value,
        effective_transfer_rate: effectiveTransferRate.value,
        transfer_rate: transferRate.value,
        formatted_total_points: formattedCalculatedPoints.value,
    };

    return { params, response };
}

async function logTransferCalculator() {
    try {
        const key = currentLogKey.value;
        if (lastLoggedKey.value === key) return; // avoid duplicate logs for same inputs

        const payload = buildLogPayload();
        if (typeof window !== 'undefined' && window.ApiService) {
            await window.ApiService.apiCall(
                '/tools/transferCalculatorLog',
                'post',
                payload,
                {
                    Accept: 'application/json',
                }
            );
            lastLoggedKey.value = key;
        }
    } catch (e) {
        // ignore logging errors
    }
}

watch(pointsToTransfer, () => {
    // reset dedupe key when input changes
    lastLoggedKey.value = null;
});

// The bonus-free display, so a sub-1 rate reads as the pair a member knows
// (7:1) rather than "1:0.14". The bonused pair is handled below.
const transferRate = computed(() =>
    ratioDisplay(props.transferPartner?.transfer_ratio)
);

const normalizedBonusMultiplier = computed(() => {
    const tp = props.transferPartner || {};
    if (typeof tp.bonus_multiplier === 'number') return tp.bonus_multiplier;
    if (typeof tp.bonus_ratio === 'number') {
        return bonusMultiplier(tp.bonus_ratio);
    }
    return null;
});

const bonusActive = computed(() => {
    const m = normalizedBonusMultiplier.value;
    return typeof m === 'number' && m > 1;
});

const bonusText = computed(() => {
    if (!bonusActive.value) return '';
    const m = normalizedBonusMultiplier.value || 1;
    const bonusPercentage = Math.round((m - 1) * 100);
    const endDate = props.transferPartner.bonus_end_date;

    if (endDate) {
        const date = new Date(endDate);
        const formattedDate = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
        });
        return `+${bonusPercentage}% until ${formattedDate}`;
    }

    return `+${bonusPercentage}% bonus`;
});

const bonusPercentageLabel = computed(() => {
    if (!bonusActive.value) return '';
    const m = normalizedBonusMultiplier.value || 1;
    const pct = Math.round((m - 1) * 100);
    return `+${pct}% bonus`;
});

const baseRatio = computed(() => {
    if (!props.transferPartner?.transfer_ratio) return 1;

    const ratioString = String(props.transferPartner.transfer_ratio);

    if (ratioString.includes(':')) {
        const ratioParts = ratioString.split(':');
        if (ratioParts.length === 2) {
            const numerator = parseFloat(ratioParts[0]) || 1;
            const denominator = parseFloat(ratioParts[1]) || 1;
            return denominator / numerator;
        }
    }

    const numericRatio = parseFloat(ratioString);
    return isNaN(numericRatio) ? 1 : numericRatio;
});

// Fallback for callers that pass a partner without the server's display
// fields. A bonus scales the transfer, mirroring
// TransferRatioCalculator::effectiveMultiplier: a 2.0 partner with a 0.3 bonus
// transfers at 2.6, which is what RewardsCC's totalRatio reports.
const localRatioPair = computed(() => {
    const multiplier = bonusActive.value
        ? normalizedBonusMultiplier.value || 1
        : 1;

    return ratioDisplayPair(baseRatio.value, baseRatio.value * multiplier);
});

const baseTransferRate = computed(
    () =>
        props.transferPartner?.base_transfer_ratio || localRatioPair.value.base
);

const effectiveTransferRate = computed(
    () =>
        props.transferPartner?.effective_transfer_ratio ||
        localRatioPair.value.effective
);

// Error management functions (matching trips pattern)
const setFormError = (field, message) => {
    calculatorErrors.value[field] = message;
};

const clearError = (field) => {
    if (calculatorErrors.value[field]) {
        delete calculatorErrors.value[field];
    }
};

const clearErrors = () => {
    calculatorErrors.value = {};
};

// Validation logic
const validateInput = (value) => {
    clearError('points_to_transfer');

    if (!value || value === '') {
        return true;
    }

    const numericValue = parseFloat(value);

    // Check minimum amount (1,000)
    if (numericValue < 1000) {
        setFormError(
            'points_to_transfer',
            'Minimum transfer amount is 1,000 points'
        );
        return false;
    }

    // Check if multiple of 1000
    if (numericValue % 1000 !== 0) {
        setFormError('points_to_transfer', 'Value must be a multiple of 1,000');
        return false;
    }

    return true;
};

// Debounced validation
const debouncedValidation = (value) => {
    if (debounceTimer.value) {
        clearTimeout(debounceTimer.value);
    }

    isValidating.value = true;

    debounceTimer.value = setTimeout(() => {
        validateInput(value);
        isValidating.value = false;
    }, 1000);
};

const isValidInput = computed(() => {
    return (
        !calculatorErrors.value.points_to_transfer &&
        pointsToTransfer.value &&
        !isValidating.value
    );
});

const hasInputError = computed(() => {
    return !!calculatorErrors.value.points_to_transfer;
});

watch(isValidInput, (valid) => {
    if (valid) {
        hasEverBeenValid.value = true;
    }
});

const standardPoints = computed(() => {
    if (
        !isValidInput.value ||
        !pointsToTransfer.value ||
        isNaN(pointsToTransfer.value)
    )
        return 0;
    const points = parseFloat(pointsToTransfer.value);
    return Math.round(points * baseRatio.value);
});

const bonusPoints = computed(() => {
    if (!bonusActive.value || !isValidInput.value || !pointsToTransfer.value)
        return 0;
    const base = parseFloat(pointsToTransfer.value);
    const m = normalizedBonusMultiplier.value || 1;
    // The bonus is the extra destination currency over the normal conversion,
    // so standardPoints + bonusPoints equals points x effectiveTransferRate.
    // 1,000 pts at 2.0 with a 30% bonus => 2,000 + 600 = 2,600 (i.e. 2.6).
    return Math.round(base * baseRatio.value * (m - 1));
});

const calculatedPoints = computed(() => {
    if (!isValidInput.value) return 0;
    return standardPoints.value + bonusPoints.value;
});

const formattedCalculatedPoints = computed(() => {
    if (!pointsToTransfer.value || !isValidInput.value)
        return 'Points to receive';
    return calculatedPoints.value.toLocaleString('en-US');
});

const handleInputChange = (event) => {
    const value = event.target.value;
    const numbers = value.replace(/\D/g, '');

    if (numbers === '') {
        pointsToTransfer.value = '';
        formattedInput.value = '';
        clearError('points_to_transfer');
        if (debounceTimer.value) {
            clearTimeout(debounceTimer.value);
        }
        return;
    }

    pointsToTransfer.value = numbers;

    const formatted = parseInt(numbers).toLocaleString('en-US');
    formattedInput.value = formatted;

    debouncedValidation(numbers);
};

const handleBlur = () => {
    if (debounceTimer.value) {
        clearTimeout(debounceTimer.value);
        debounceTimer.value = null;
    }

    if (pointsToTransfer.value) {
        const isValid = validateInput(pointsToTransfer.value);
        // Log the transfer calculation when user finishes entering a valid amount
        if (isValid && !hasInputError.value) {
            logTransferCalculator();
        }
    }
    isValidating.value = false;
};

const closeModal = () => {
    emit('close');
    pointsToTransfer.value = '';
    formattedInput.value = '';
    hasEverBeenValid.value = false;
    clearErrors();
};

const handleEscapeKey = (event) => {
    if (event.key === 'Escape' && props.isOpen) {
        closeModal();
    }
};

onMounted(() => {
    document.addEventListener('keydown', handleEscapeKey);
});

onUnmounted(() => {
    document.removeEventListener('keydown', handleEscapeKey);
});

watch(
    () => props.isOpen,
    (newValue) => {
        if (!newValue) {
            pointsToTransfer.value = '';
        }
    }
);
</script>
